import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Calculator, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ALL_ROLE_CONFIG } from '@/lib/equip-program-config';

// Local adapter type — maps canonical ProgramRoleConfig shape to the fields
// used by AwardCalculator. This keeps downstream code unchanged while sourcing
// data from the canonical config.
interface RoleConfig {
  name: string;
  fullTime: number;
  perDay: number;
  perHour?: number;
  category: 'physician' | 'apc' | 'teletriage';
}

function toAwardCalculatorConfig(id: string): RoleConfig {
  const canonical = ALL_ROLE_CONFIG[id as keyof typeof ALL_ROLE_CONFIG];
  if (!canonical) throw new Error(`Unknown role: ${id}`);
  return {
    name: canonical.name,
    fullTime: canonical.maxAward.fullTime,
    perDay: canonical.maxAward.perDayPartTime,
    perHour: canonical.maxAward.perHourPartTime,
    category:
      canonical.category === 'physician'
        ? 'physician'
        : canonical.id === 'teletriage'
          ? 'teletriage'
          : 'apc',
  };
}

// Derived from canonical config — single source of truth.
const roleConfigs: Record<string, RoleConfig> = Object.fromEntries(
  Object.keys(ALL_ROLE_CONFIG).map((id) => [id, toAwardCalculatorConfig(id)])
);

interface AwardCalculatorProps {
  simplified?: boolean;
  defaultRole?: string;
  /** When set, initializes employment status and hides the toggle for FT users. */
  effectiveEmploymentStatus?: import('@/data/types').EmploymentStatus;
  className?: string;
}

export function AwardCalculator({
  simplified = false,
  defaultRole,
  effectiveEmploymentStatus,
  className,
}: AwardCalculatorProps) {
  const [role, setRole] = useState(defaultRole || 'primary-care-physician');
  const [isFullTime, setIsFullTime] = useState(effectiveEmploymentStatus !== 'part-time');
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [hoursPerWeek, setHoursPerWeek] = useState(30);
  const [displayedAward, setDisplayedAward] = useState(0);
  const animationRef = useRef<number | null>(null);

  const config = roleConfigs[role];
  const isTeletriage = config.category === 'teletriage';
  const maxAward = isFullTime
    ? config.fullTime
    : isTeletriage && config.perHour
      ? Math.round(config.perHour * hoursPerWeek)
      : config.perDay * daysPerWeek;
  const qualityAmount = Math.round(maxAward * 0.6);
  const coreAmount = Math.round(maxAward * 0.4);

  // Smooth CountUp animation using requestAnimationFrame
  useEffect(() => {
    const startValue = displayedAward;
    const endValue = maxAward;
    const duration = 800;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(startValue + (endValue - startValue) * eased);

      setDisplayedAward(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [maxAward]);

  const dayLabel = daysPerWeek === 1 ? '1 day' : `${daysPerWeek} days`;

  return (
    <Card
      className={cn(
        'overflow-hidden border-2 transition-all duration-300 hover:border-eventus-purple/30 hover:shadow-lg',
        className
      )}
    >
      <CardHeader className="bg-gradient-to-r from-eventus-purple/5 via-transparent to-eventus-teal/5 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-eventus-purple/10">
            <Calculator className="w-5 h-5 text-eventus-purple" />
          </div>
          <CardTitle className="text-xl font-bold text-eventus-navy">
            Maximum Quarterly Award
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Role Selector */}
        <div className="space-y-2">
          <label className="text-base font-medium text-gray-700">Select Role</label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="primary-care-physician">Physician</SelectItem>
              <SelectItem value="primary-care-apc">Primary Care APC</SelectItem>
              <SelectItem value="psychiatry-apc">Psychiatry APC</SelectItem>
              <SelectItem value="palliative-care-apc">Palliative Care APC</SelectItem>
              <SelectItem value="teletriage">Teletriage</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Employment Toggle — hidden when locked to a specific status */}
        {!effectiveEmploymentStatus && (
          <div className="flex items-center justify-between py-2">
            <span className="text-base font-medium text-gray-700">Employment Status</span>
            <div className="flex items-center gap-3">
              <motion.span
                animate={{
                  color: !isFullTime ? '#877be9' : '#9ca3af',
                  fontWeight: !isFullTime ? 600 : 400,
                }}
                className="text-base transition-colors"
              >
                Part-Time
              </motion.span>
              <Switch checked={isFullTime} onCheckedChange={setIsFullTime} />
              <motion.span
                animate={{
                  color: isFullTime ? '#877be9' : '#9ca3af',
                  fontWeight: isFullTime ? 600 : 400,
                }}
                className="text-base transition-colors"
              >
                Full-Time
              </motion.span>
            </div>
          </div>
        )}

        {/* Days Per Week Slider (Part-time only) */}
        <AnimatePresence mode="wait">
          {!isFullTime && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 0 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="space-y-3 pt-2 pb-4 px-4 bg-gray-50 rounded-lg">
                {isTeletriage ? (
                  <>
                    <div className="flex justify-between items-center">
                      <label className="text-base font-medium text-gray-700">
                        Hours per Week
                      </label>
                      <motion.span
                        key={hoursPerWeek}
                        initial={{ scale: 1.2, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-lg font-bold text-eventus-purple"
                      >
                        {hoursPerWeek} hours
                      </motion.span>
                    </div>
                    <Slider
                      value={[hoursPerWeek]}
                      onValueChange={([v]) => setHoursPerWeek(v)}
                      min={8}
                      max={39}
                      step={1}
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>8</span>
                      <span>16</span>
                      <span>24</span>
                      <span>32</span>
                      <span>39</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <label className="text-base font-medium text-gray-700">
                        Days per Week
                      </label>
                      <motion.span
                        key={daysPerWeek}
                        initial={{ scale: 1.2, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-lg font-bold text-eventus-purple"
                      >
                        {dayLabel}
                      </motion.span>
                    </div>
                    <Slider
                      value={[daysPerWeek]}
                      onValueChange={([v]) => setDaysPerWeek(v)}
                      min={0}
                      max={5}
                      step={0.01}
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>0</span>
                      <span>1</span>
                      <span>2</span>
                      <span>3</span>
                      <span>4</span>
                      <span>5</span>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Award Display */}
        <motion.div
          layout
          className="text-center py-8 px-4 bg-gradient-to-br from-eventus-navy/5 to-eventus-purple/5 rounded-xl"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-eventus-purple" />
            <span className="text-base font-medium text-gray-600">Maximum Potential</span>
          </div>
          <motion.div
            className="text-5xl md:text-6xl font-bold text-eventus-navy tracking-tight"
            layout
          >
            <span className="text-eventus-purple">$</span>
            <span className="tabular-nums">{displayedAward.toLocaleString()}</span>
          </motion.div>
          <p className="text-gray-500 mt-2 text-base">per quarter</p>
          {!isFullTime && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-eventus-purple mt-1"
            >
              {isTeletriage
                ? `$${config.perHour} per hour x ${hoursPerWeek} hours`
                : `$${config.perDay.toLocaleString()} per day x ${daysPerWeek} days`}
            </motion.p>
          )}
        </motion.div>

        {/* 60/40 Breakdown */}
        {!simplified && (
          <div className="space-y-3">
            <div className="flex justify-between text-base font-medium">
              <span className="text-eventus-purple flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-eventus-purple" />
                Quality Measures (60%)
              </span>
              <span className="text-eventus-navy flex items-center gap-2">
                Core Measures (40%)
                <span className="w-3 h-3 rounded-full bg-eventus-navy" />
              </span>
            </div>

            <div className="relative h-6 rounded-full overflow-hidden bg-gray-100">
              <motion.div
                className="absolute left-0 top-0 h-full bg-eventus-purple"
                initial={{ width: 0 }}
                animate={{ width: '60%' }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              />
              <motion.div
                className="absolute right-0 top-0 h-full bg-eventus-navy"
                initial={{ width: 0 }}
                animate={{ width: '40%' }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
              />
            </div>

            <div className="flex justify-between text-base">
              <motion.span
                key={qualityAmount}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                className="text-eventus-purple font-semibold"
              >
                ${qualityAmount.toLocaleString()}
              </motion.span>
              <motion.span
                key={coreAmount}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                className="text-eventus-navy font-semibold"
              >
                ${coreAmount.toLocaleString()}
              </motion.span>
            </div>
          </div>
        )}

        {/* Annual projection */}
        <div className="pt-4 border-t border-gray-100">
          <div className="flex justify-between items-center text-base">
            <span className="text-gray-500">Annual potential (4 quarters)</span>
            <motion.span
              key={maxAward * 4}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              className="font-bold text-eventus-navy"
            >
              ${(maxAward * 4).toLocaleString()}
            </motion.span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
